import React, { useCallback, useEffect, useRef, useState } from 'react';
import AppLayout from '../../../../component/appLayout';
import {
  Group,
  Content,
  Text,
  FormBlock,
  Header,
  PersonalText,
  UserProfile,
  Icon,
  UploadText,
  ImageUploadText,
  Title1,
  EditIcon,
  OrganizationForm,
  OrganizationFormDetails,
  OrganizationDeleteBlock,
  WorkspaceIcon,
  OverlaySquare,
  Container
} from '../../styles';
import {
  onUpdateWorkspaceDetails,
  sendDeleteRequest,
  updateCurrentWorkspaceLocally
} from '../../../../services/workspaceService';
import Button from '../../../../component/button';
import { getFirebaseUrlFromFile, handleFileReadImage, isEmpty, isPhoneNumberValid } from '../../../../helpers/common';
import { trackAnalyticActivity } from '../../../../services/analyticsService';
import { captureException } from '../../../../services/logService';
import ResponsiveHeader from '../../../../component/responsiveHeader';
import { useMobileDevice } from '../../../../global/useMobile';
import ConfirmationModal from '../../../../component/modals/confirmationModal';
import Modal from '../../../../component/modals/modal';
import { VerificationModal } from '../../../../component/modals/verificationModal';
import { WORKSPACE_ANALYTICS } from '../../../../global/analyticsConstants';
import { rootStore } from '../../../../mobx/rootStore';
import SVGIcon from '../../../../assets/images/svg/SVGIcon';
import BottomSubNavigation from '../../../../component/bottomNavigation/subNavigation';
import { BUTTON_TYPE, ICON_SIZE, IMAGE_COMPRESSION } from '../../../../global/constants';
import BrandDomain from './brandDomain';
import { observer } from 'mobx-react-lite';
import Input from '../../../../component/input';
import Image from '../../../../component/image';

interface Props {
  secondary?: boolean;
}

interface workspaceInputInterface {
  name: string;
  email: string;
  phone: string;
}
const OrganizationProfile: React.FC<Props> = () => {
  // Refs
  const inputRef = useRef<HTMLInputElement | null>(null);

  // mobx states
  const { currentWorkspace } = rootStore.workspaceStore || {};

  // States
  const [image, setImage] = useState<
    | {
        profile: string | ArrayBuffer | null;
        file: File;
      }
    | undefined
  >();
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isVerificationOpen, setIsVerificationOpen] = useState(false);
  const [workspaceInput, setWorkspaceInput] = useState<workspaceInputInterface>({
    name: currentWorkspace?.name || '',
    email: currentWorkspace?.email || '',
    phone: currentWorkspace?.phone || ''
  });

  // Other variables
  const isMobile = useMobileDevice();

  useEffect(() => {
    setWorkspaceInput({
      name: currentWorkspace?.name || '',
      email: currentWorkspace?.email || '',
      phone: currentWorkspace?.phone || ''
    });
  }, [currentWorkspace?.name, currentWorkspace?.email, currentWorkspace?.phone]);

  // Handles file upload and validation
  const handleFileRead = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Workspace logos are hard-capped to 256x256 / 50KB before upload.
    const result = await handleFileReadImage(event, {
      maxWidth: IMAGE_COMPRESSION.AVATAR_MAX_WIDTH,
      maxHeight: IMAGE_COMPRESSION.AVATAR_MAX_HEIGHT,
      maxSizeBytes: IMAGE_COMPRESSION.AVATAR_MAX_SIZE_BYTES
    });
    if (result) {
      setImage(result);
    } else {
      // General-specific logic: reset inputRef if invalid
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  // Triggers file upload input click
  const handleUploadClick = () => {
    inputRef.current?.click();
  };

  // Handles form submission and updates workspace details
  const onSubmit = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      if (!currentWorkspace?.isAdmin) {
        rootStore.alertNotificationStore.setErrorMessage('Only admin can update details!');
        return false;
      }
      if (workspaceInput.phone && !isPhoneNumberValid(workspaceInput.phone)) {
        rootStore.alertNotificationStore.setErrorMessage('Please enter a valid phone number.');
        return false;
      }
      try {
        setLoading(true);
        let profile: string | undefined;
        if (image) {
          profile = await getFirebaseUrlFromFile(image, 'OrganizationProfiles/', 'workspace-profile');
        }

        const getLogoUrl = () => {
          if (!isEmpty(image?.profile)) return profile;
          if (!isEmpty(currentWorkspace?.logoUrl)) return currentWorkspace?.logoUrl;
          return null;
        };

        const payload = {
          name: workspaceInput.name,
          email: workspaceInput.email,
          logoUrl: getLogoUrl(),
          phone: workspaceInput.phone
        };
        const result = await onUpdateWorkspaceDetails(currentWorkspace?.id, payload);
        if (result) {
          trackAnalyticActivity(WORKSPACE_ANALYTICS.UPDATED);
          updateCurrentWorkspaceLocally({ ...payload, logoUrl: payload.logoUrl || undefined });
        }
      } catch (error) {
        captureException(error);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    },
    [workspaceInput.phone, workspaceInput.name, workspaceInput.email, image, currentWorkspace]
  );

  // Opens the confirmation modal for workspace deletion
  const handleConfirmationOpen = useCallback(() => {
    setIsConfirmationOpen(true);
  }, []);

  // Closes the confirmation modal
  const CloseConfirmationModal = useCallback(() => {
    setIsConfirmationOpen(false);
  }, []);

  // Opens the verification modal and sends delete request
  const handleVerificationModalOpen = useCallback(async () => {
    try {
      setDeleteLoading(true);
      const result = await sendDeleteRequest(false);
      if (result) {
        setIsVerificationOpen(true);
      }
    } catch (e) {
      captureException(e);
      rootStore.alertNotificationStore.setErrorMessage('Failed to send delete request. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  }, []);

  // Closes the verification modal
  const CloseVerificationModal = useCallback(() => {
    setIsVerificationOpen(false);
    setIsConfirmationOpen(false);
  }, []);

  return (
    <>
      <AppLayout>
        <Container className='desktop-content'>
          <Group as='form' onSubmit={onSubmit} isMiddleContainer={true}>
            <Content>
              {!isMobile ? (
                <Header>
                  <PersonalText>Organisation Profile</PersonalText>
                </Header>
              ) : (
                <ResponsiveHeader title='Organisation Profile' />
              )}
              {/* organisation profile section */}
              <OrganizationForm>
                <OrganizationFormDetails>
                  <UserProfile>
                    <input type='file' onChange={handleFileRead} ref={inputRef} hidden aria-label='Upload file' />
                    <Icon
                      className={
                        !isEmpty(image?.profile) || !isEmpty(currentWorkspace?.logoUrl)
                          ? 'square-profile-image-container'
                          : ''
                      }>
                      {!isEmpty(image?.profile) || !isEmpty(currentWorkspace?.logoUrl) ? (
                        <Image
                          src={
                            !isEmpty(image?.profile) && image?.profile !== null && typeof image?.profile === 'string'
                              ? image?.profile
                              : currentWorkspace?.logoUrl || ''
                          }
                          alt='googleLogo'
                          width={46}
                          height={46}
                          className='square-profile-image'
                        />
                      ) : (
                        <WorkspaceIcon className='workspace-icon-wrapper'>
                          <SVGIcon
                            name='workspace-icon'
                            width={ICON_SIZE.XXL}
                            height={ICON_SIZE.XXL}
                            viewBox='0 0 36 36'
                            className='workspace-icon'
                          />
                        </WorkspaceIcon>
                      )}
                      <OverlaySquare
                        role='button'
                        className={
                          !isEmpty(image?.profile) || !isEmpty(currentWorkspace?.logoUrl)
                            ? 'edit-profile'
                            : 'empty-edit-profile'
                        }
                        onClick={handleUploadClick}>
                        <EditIcon>
                          <SVGIcon
                            name='edit-icon'
                            width='26'
                            height='26'
                            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                            fill='var(--text-white)'
                          />
                        </EditIcon>
                      </OverlaySquare>
                    </Icon>

                    <UploadText>
                      <ImageUploadText role='button' onClick={handleUploadClick}>
                        Upload new image
                      </ImageUploadText>
                      <Text> Maximum file size allowed in 500 KB in png, jpeg, jpg format. </Text>
                    </UploadText>
                  </UserProfile>
                  <FormBlock>
                    <Input
                      label='Organization Name'
                      value={workspaceInput?.name || ''}
                      onChange={(e) => setWorkspaceInput({ ...workspaceInput, name: e.target.value })}
                      fieldWrapperWidth='260px'
                    />
                    <Input
                      label='Mobile Number'
                      value={workspaceInput?.phone || ''}
                      onChange={(e) => setWorkspaceInput({ ...workspaceInput, phone: e.target.value })}
                      fieldWrapperWidth='260px'
                    />
                    <Input
                      label='Email'
                      value={workspaceInput?.email || ''}
                      onChange={(e) => setWorkspaceInput({ ...workspaceInput, email: e.target.value })}
                      fieldWrapperWidth='260px'
                    />
                  </FormBlock>
                  <Button
                    title='Update'
                    variant={BUTTON_TYPE.PRIMARY}
                    buttonType='submit'
                    isLoading={loading}
                    disabled={loading}
                  />
                </OrganizationFormDetails>
                {/* brand & domain section */}
                <BrandDomain />
                {/* delete workspace section */}
                <OrganizationFormDetails isDelete>
                  <OrganizationDeleteBlock>
                    <Title1>Delete workspace</Title1>
                    <Text className='text'>
                      As an administrator, you have the ability to delete this workspace. This action can't be undone.
                    </Text>
                  </OrganizationDeleteBlock>
                  <Button
                    variant={BUTTON_TYPE.SECONDARY}
                    title='Delete workspace'
                    onClick={(e: React.SyntheticEvent) => {
                      handleConfirmationOpen();
                      e.preventDefault();
                    }}
                  />
                </OrganizationFormDetails>
              </OrganizationForm>
            </Content>
          </Group>
        </Container>
      </AppLayout>
      <Modal open={isConfirmationOpen} onClose={CloseConfirmationModal} maxWidth={'346px'}>
        <ConfirmationModal
          isDelete
          loading={deleteLoading}
          modalTitle='Delete workspace?'
          description='Are you sure you want to delete this workspace?'
          onClose={CloseConfirmationModal}
          onOk={() => handleVerificationModalOpen()}
        />
      </Modal>
      <Modal open={isVerificationOpen} onClose={CloseVerificationModal} maxWidth={'462px'}>
        <VerificationModal onClose={CloseVerificationModal} />
      </Modal>
      <BottomSubNavigation />
    </>
  );
};

export default observer(OrganizationProfile);
